/* ============================================================
   Taglio — homepage section behaviour
   Mounts TaglioTag instances declared in markup, so Liquid can stay
   declarative and every section reuses one mounter.

   Markup contract:
     <div data-tg-tag='{"baseColor":"black","name":"BELLA",...}'></div>

   The JSON is parsed from the attribute, so merchants/editors changing a
   section setting changes the tag without touching JS.

   Optional per-instance overrides handled here (not in the renderer):
     - "sizeMode": "hero" | "card"  -> responsive size via matchMedia
     - "data-tg-rotate" (attribute, not JSON) -> the hero's timed look-swap
     - "data-tg-personalize" (attribute, not JSON) -> follow the visitor's pet
       name, typed once on the homepage hero and remembered for the visit
   ============================================================ */
(function () {
  'use strict';

  // Loaded once by layout/theme.liquid — but the generated preview renders that
  // layout twice (desktop + mobile frames) and appends its own copies, so this
  // can execute more than once on a page. Each execution would build its own
  // rotator registry, leaving window.TaglioSections pointing at the last (empty)
  // one: the intervals still run, but the exposed API reads as dead. First load
  // wins; later loads stand down.
  if (window.TaglioSections) return;

  var MQ = '(min-width: 760px)';

  function parse(node) {
    var raw = node.getAttribute('data-tg-tag');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      if (window.console) console.warn('[taglio] bad data-tg-tag JSON', e);
      return null;
    }
  }

  // Responsive sizes come from the handoff's compact/desktop pairs.
  var SIZE_PAIRS = {
    hero: [280, 460],
    how: [160, 200],
    drop: [130, 210],
    collection: [108, 120],
    refresh: [220, 300],
    why: [240, 340],
    // Build page cross-sell cards (handoff: size={compact ? 120 : 150}).
    cross: [120, 150],
    // Cart line thumbnails (handoff: size={compact ? 84 : 100}).
    cart: [84, 100],
    // The small tags inside the cart's free-shipping nudge (handoff: 56).
    nudge: [50, 56],
  };

  // ---- hero look rotation ------------------------------------------------
  // The homepage hero can cycle its look on a timer: a new base, a contrasting
  // nameplate and icon colour, a new icon, and the next pet name walking A–Z
  // through the store's top names. The section opts in with data-tg-rotate="5"
  // (seconds); every other mount point behaves exactly as before.
  //
  // Colour pairs are picked by MEASURED contrast (TaglioCore.contrastRatio), not
  // by hand, because "random colour + random colour" regularly produces two
  // colours that read as one. Each colour is chosen against what it actually
  // sits on: the plate against the base, the icons against the base, the name
  // against the plate.
  //
  // Three guards keep it from misbehaving:
  //   * prefers-reduced-motion stops the timer entirely
  //   * a hidden tab stops the timer (no work while nobody is looking)
  //   * the Shopify theme editor never starts it (the merchant is mid-edit)
  var ROTATE_MIN_SECONDS = 2;
  var inEditor = !!(window.Shopify && window.Shopify.designMode);
  var rotators = [];

  function makeRotator(tag, seconds) {
    var Core = window.TaglioCore;
    if (!Core || !Core.PET_NAMES || !Core.pickContrasting) return null;
    var names = Core.PET_NAMES;
    var keys = Object.keys(Core.FILAMENTS || {});
    var icons = Object.keys(Core.ICON_PATTERNS || {});
    if (!keys.length || !icons.length || !names.length) return null;

    var period = Math.max(ROTATE_MIN_SECONDS, Number(seconds) || 5) * 1000;
    // Walk the alphabet instead of picking letters at random, so the preview
    // shows the whole A–Z list over time rather than clustering on a few letters.
    var letter = Math.floor(Math.random() * 26);
    var timer = null;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    function pickName() {
      var start = letter * 4;
      letter = (letter + 1) % 26;
      var pool = names.slice(start, start + 4);
      if (!pool.length) pool = names;
      return pool[Math.floor(Math.random() * pool.length)].toUpperCase();
    }

    function nextLook() {
      var base = keys[Math.floor(Math.random() * keys.length)];
      var plate = Core.pickContrasting(base, keys, 3);
      // Icons sit on the base too, so keep them off the plate's colour as well.
      var iconColor = Core.pickContrasting(base, keys, 2.5, [plate]);
      var textColor = Core.pickContrasting(plate, keys, 4.5);
      return {
        baseColor: base,
        nameplateColor: plate,
        iconColor: iconColor,
        textColor: textColor,
        icon: icons[Math.floor(Math.random() * icons.length)],
        // A visitor who typed their pet's name keeps it while the look keeps
        // changing; only the colours and icon cycle. With no name stored this
        // falls through to the A–Z walk, which is the original behaviour.
        name: readPetName() || pickName(),
      };
    }

    // The hero field calls this so a typed name lands at once instead of up to
    // `period` seconds later. An empty name resumes the A–Z walk.
    function setName(name) {
      tag.update({ name: name || pickName() });
    }

    function start() {
      if (timer || reduced.matches || document.hidden || inEditor) return;
      timer = window.setInterval(function () { tag.update(nextLook()); }, period);
    }
    function stop() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    }
    function onVisibility() { if (document.hidden) stop(); else start(); }
    function onReduced() { if (reduced.matches) stop(); else start(); }

    document.addEventListener('visibilitychange', onVisibility);
    if (reduced.addEventListener) reduced.addEventListener('change', onReduced);
    else if (reduced.addListener) reduced.addListener(onReduced);

    start();
    return { start: start, stop: stop, nextLook: nextLook, setName: setName, period: period };
  }

  // ---- visitor pet name ---------------------------------------------------
  // The homepage hero lets a visitor type their pet's name once. Every tag that
  // opted in then shows it, the Build page pre-fills its name field, and the
  // cart follows it where the ordered line has no name of its own.
  //
  // SESSION storage, not local: the name is a convenience for the current visit,
  // not a remembered profile. It follows the visitor from page to page, then is
  // gone when the tab closes — so a shared computer never shows the next visitor
  // someone else's pet name. Reads go through an accessor so a browser that
  // blocks storage (Safari private mode, cookies disabled) degrades to "no name"
  // rather than throwing on every page.
  var PET_NAME_KEY = (window.TaglioCore && window.TaglioCore.PET_NAME_KEY) || 'taglio:petName';
  var PET_NAME_EVENT = 'taglio:petname';

  // Every mounted tag that opted in, with the mode it opted in with.
  var personalizable = [];

  function readPetName() {
    try { return String(window.sessionStorage.getItem(PET_NAME_KEY) || '').trim(); }
    catch (e) { return ''; }
  }

  // Match the Build page's own normalisation (setName): collapse whitespace, cap
  // at 10, uppercase. A name that reaches the nameplate has to fit it.
  function normaliseName(v) {
    return String(v == null ? '' : v).replace(/\s+/g, ' ').trim().slice(0, 10).toUpperCase();
  }

  // The mode decides precedence:
  //   override -> the visitor's name replaces the tag's own. Decorative tags
  //               carry a made-up sample name, so the visitor should win.
  //   fallback -> the tag keeps its own name and only borrows the visitor's when
  //               it has none. Real cart lines show the name that was ordered.
  function nameFor(entry, name) {
    if (!name) return entry.ownName;
    return entry.mode === 'fallback' ? (entry.ownName || name) : name;
  }

  function applyPetName(name) {
    personalizable.forEach(function (entry) {
      var want = nameFor(entry, name);
      if (entry.current === want) return;
      entry.current = want;
      entry.tag.update({ name: want });
    });
  }

  function writePetName(v) {
    var name = normaliseName(v);
    try {
      if (name) window.sessionStorage.setItem(PET_NAME_KEY, name);
      else window.sessionStorage.removeItem(PET_NAME_KEY);
    } catch (e) { /* storage blocked; the in-page apply below still runs */ }
    applyPetName(name);
    // The hero's own tag is driven by its rotator, not by applyPetName, so pin it
    // here too. Any other rotator would follow the same rule.
    rotators.forEach(function (r) { r.setName(name); });
    // The Build island lives on another page and needs a signal the moment the
    // name changes while it is mounted. sessionStorage's `storage` event only
    // reaches other tabs (and is not reliable for sessionStorage at all), so this
    // is a plain window event.
    try {
      window.dispatchEvent(new CustomEvent(PET_NAME_EVENT, { detail: { name: name } }));
    } catch (e) {}
    return name;
  }

  // ---- hero pet-name field ------------------------------------------------
  // The field above the hero tag. Typing writes the shared store, which repaints
  // every opted-in tag on the page and pins the hero rotator; clearing it puts
  // the hero back to its A–Z walk. Progressive enhancement: without JS the form
  // simply does nothing and the hero still cycles on its own.
  //
  // querySelectorAll, not querySelector: the generated preview renders the layout
  // twice (desktop + mobile frames), so there can be more than one of these. The
  // live theme has one, but a second field that silently does nothing is exactly
  // the kind of bug that only shows up in the preview the merchant is judging.
  function wireHeroName() {
    var forms = document.querySelectorAll('[data-tg-hero-name]');
    if (!forms.length) return;
    var inputs = [];

    Array.prototype.forEach.call(forms, function (form) {
      var input = form.querySelector('input');
      if (!input) return;
      inputs.push(input);

      function push() {
        var name = writePetName(input.value);
        // writePetName normalises; write the canonical value back so the field
        // and the tags never disagree about case or length.
        if (input.value !== name) input.value = name;
      }

      input.addEventListener('input', push);
      form.addEventListener('submit', function (e) { e.preventDefault(); push(); });
    });

    if (!inputs.length) return;

    // Reflect what is already stored (back navigation, or a second field on the
    // same page) so no field misrepresents the state.
    function syncFields() {
      var name = readPetName();
      inputs.forEach(function (i) { if (i.value !== name) i.value = name; });
    }
    syncFields();
    window.addEventListener(PET_NAME_EVENT, syncFields);
  }

  function mountAll() {
    if (!window.TaglioTag) return;
    var nodes = document.querySelectorAll('[data-tg-tag]');
    var mql = window.matchMedia(MQ);
    var mounted = [];

    Array.prototype.forEach.call(nodes, function (node) {
      if (node.getAttribute('data-tg-mounted') === '1') return;
      var props = parse(node);
      if (!props) return;

      var mode = props.sizeMode;
      delete props.sizeMode;
      var pair = SIZE_PAIRS[mode];
      if (pair) props.size = mql.matches ? pair[1] : pair[0];

      var tag;
      try {
        tag = window.TaglioTag.create(props);
      } catch (e) {
        if (window.console) console.warn('[taglio] tag create failed', e);
        return;
      }
      node.appendChild(tag.el);
      node.setAttribute('data-tg-mounted', '1');
      mounted.push({ node: node, tag: tag, pair: pair });

      // Opt-in personalization. The attribute carries the MODE, not a boolean,
      // because the two callers disagree about precedence: decorative tags let
      // the visitor win, real cart lines keep the ordered name. `props.name` is
      // the tag's own name, captured before any personalization so 'fallback'
      // can fall back to it.
      var pz = node.getAttribute('data-tg-personalize');
      if (pz) {
        personalizable.push({
          tag: tag,
          mode: pz,
          ownName: props.name || '',
          current: props.name || '',
        });
      }

      // Opt-in look rotation. The attribute lives on the wrapper (see the
      // comment in snippets/taglio-tag-data.liquid); its value is the period.
      var rot = node.getAttribute('data-tg-rotate');
      if (rot != null) {
        var rotator = makeRotator(tag, rot);
        if (rotator) rotators.push(rotator);
      }
    });

    // Apply the visitor's stored name to everything that opted in, including
    // tags that were already in the DOM before this run.
    if (personalizable.length) applyPetName(readPetName());

    // Keep sizes correct across the 760px breakpoint without a reload.
    if (!mounted.length) return;
    function onBreakpoint(ev) {
      mounted.forEach(function (m) {
        if (!m.pair) return;
        m.tag.update({ size: ev.matches ? m.pair[1] : m.pair[0] });
      });
    }
    if (mql.addEventListener) mql.addEventListener('change', onBreakpoint);
    else if (mql.addListener) mql.addListener(onBreakpoint);
  }

  function boot() {
    mountAll();
    wireHeroName();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.TaglioSections = {
    mountAll: mountAll,
    // The hero field and the Build island share this one store, so it is exposed
    // rather than duplicated in taglio-build.js.
    readPetName: readPetName,
    writePetName: writePetName,
    normaliseName: normaliseName,
    PET_NAME_KEY: PET_NAME_KEY,
    PET_NAME_EVENT: PET_NAME_EVENT,
    // Exposed so the verification harness (and anything else) can drive one
    // look-swap without waiting out the real interval.
    nextLookFor: function (i) { return rotators[i] ? rotators[i].nextLook() : null; },
    setNameFor: function (i, name) { if (rotators[i]) rotators[i].setName(name); },
    rotatorCount: function () { return rotators.length; },
  };
})();
